import { getApiUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

// Shared by the Media page's track table, the Home dashboard's Now
// Playing player, and the sidebar's mini now-playing box — one place
// for "does this track have real cover art, and if not, what placeholder
// do we show" rather than three slightly different copies.
export function AlbumArt({
  trackId,
  hasCoverArt,
  size = "md",
  className,
  version,
}: {
  trackId: string | null | undefined;
  hasCoverArt: boolean | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
  // Changes when the picture was replaced, so the browser fetches it again.
  version?: string | number;
}) {
  const sizeClass = size === "sm" ? "h-10 w-10" : size === "lg" ? "h-28 w-28" : "h-[34px] w-[34px]";
  const iconSizeClass = size === "lg" ? "h-10 w-10" : size === "sm" ? "h-5 w-5" : "h-4 w-4";

  if (!trackId || !hasCoverArt) {
    // No embedded album art on this file (or nothing playing yet) — a
    // plain placeholder tile rather than a broken image icon.
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md bg-bg-main text-text-tertiary",
          sizeClass,
          className
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className={iconSizeClass}
        >
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element -- points at the
    // API's own image-proxy endpoint (GET /tracks/{id}/cover, deliberately
    // unauthenticated — see that route's docstring), not a static/local
    // asset next/image is built to optimize.
    <img
      src={`${getApiUrl()}/tracks/${trackId}/cover${version !== undefined ? `?v=${version}` : ""}`}
      alt=""
      className={cn("shrink-0 rounded-md object-cover", sizeClass, className)}
    />
  );
}
